"use client";

import React from "react";
import { Flame, ShieldAlert, Droplets, Wind, ArrowRight, PhoneCall, Clock } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
const damageLayers = [{
  title: "Corrosive soot and chemical etching",
  description: "Within hours, synthetic materials burned in a fire produce acidic soot that permanently etches glass, tarnishes polished metals, and discolors marble and tile finishes.",
  icon: Flame
}, {
  title: "Firefighting water saturation",
  description: "Thousands of gallons used to extinguish flames pool inside floor cavities and drywall, creating immediate structural rot and secondary mold threats.",
  icon: Droplets
}, {
  title: "Compromised structural framing",
  description: "Charred load-bearing timber and warped metal joists require systematic evaluation, shoring, and certified rebuilding to restore physical integrity.",
  icon: ShieldAlert
}, {
  title: "Pervasive microscopic smoke odor",
  description: "Combustion gases pressurize into wall cavities, insulation, and HVAC duct systems, requiring thermal fogging and hydroxyl neutralization rather than surface masking.",
  icon: Wind
}];
export default function Overview() {
  const shouldReduceMotion = useReducedMotion();
  const animationProps = shouldReduceMotion ? {
    initial: {
      opacity: 1,
      y: 0
    }
  } : {
    initial: {
      opacity: 0,
      y: 16
    },
    whileInView: {
      opacity: 1,
      y: 0
    },
    viewport: {
      once: true,
      margin: "-40px"
    },
    transition: {
      duration: 0.35,
      ease: [0.16, 1, 0.3, 1]
    }
  };
  return <section id="overview" data-nav="light" className="relative bg-background text-foreground py-16 md:py-24 border-b border-border overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Technical Spec Bar Header */}
        <div className="flex flex-wrap items-center justify-between gap-4 pb-6 mb-12 border-b border-border">
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-sm bg-primary/10 border border-primary/25 text-primary text-xs font-mono font-semibold uppercase tracking-wider">
              <span className="size-1.5 rounded-full bg-primary animate-pulse" />
              Technical overview
            </span>
            <span className="text-xs font-mono text-muted-foreground uppercase tracking-wider">
              Fire &bull; Soot &bull; Water &bull; Odor
            </span>
          </div>

          <div className="flex items-center gap-4 text-xs font-mono text-muted-foreground">
            <span className="flex items-center gap-1.5">
              <Clock className="size-3.5 text-primary" />
              Response target: &lt; 60 mins
            </span>
            <span className="hidden sm:inline-block text-border">|</span>
            <span className="hidden sm:inline-block">MD &bull; DC &bull; Northern VA</span>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {/* Left Column: Narrative and Destruction Layers */}
          <motion.div {...animationProps} className="lg:col-span-7 flex flex-col gap-6">
            <div className="space-y-3">
              <h2 className="font-default text-3xl sm:text-4xl font-bold tracking-tight text-foreground leading-tight">
                Fire damage doesn't stop when the flames are out
              </h2>
              <p className="text-lg text-muted-foreground leading-relaxed">
                Acidic soot, water from fire hoses, and toxic smoke compounds continue eroding your building materials until actively contained.
              </p>
            </div>

            <p className="text-foreground/90 leading-relaxed">
              Every structural fire creates a compounding chain reaction. While the fire department successfully neutralizes the immediate flame, the lingering heat creates high indoor air pressure that drives corrosive gases deep into subfloors, crawl spaces, and mechanical ductwork. Left untreated for even 48 hours, acidic residue leaves permanent pitting on plumbing fixtures, appliances, and architectural stone that standard cleaners cannot reverse.
            </p>

            {/* Compounding Damage List */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-2">
              {damageLayers.map((layer, index) => {
              const Icon = layer.icon;
              return <Card key={layer.title} data-index={index} className="border border-border bg-card p-4 rounded-md shadow-xs transition-[border-color,box-shadow] duration-200 hover:border-primary/40 hover:shadow-sm">
                    <CardContent className="p-0 flex flex-col gap-2">
                      <div className="size-8 rounded-sm bg-primary/10 flex items-center justify-center text-primary shrink-0">
                        <Icon className="size-4.5" />
                      </div>
                      <h3 className="font-default font-semibold text-base text-foreground leading-snug">
                        {layer.title}
                      </h3>
                      <p className="text-xs text-muted-foreground leading-relaxed">
                        {layer.description}
                      </p>
                    </CardContent>
                  </Card>;
            })}
            </div>

            {/* Emergency Action Strip */}
            <div className="pt-4 flex flex-wrap items-center gap-4">
              <Button asChild size="lg" className="bg-primary text-primary-foreground font-semibold px-6 hover:bg-primary/90">
                <a href="tel:855-435-6631">
                  <PhoneCall className="size-4 mr-2" />
                  Call 855-435-6631 directly
                </a>
              </Button>
              <Button asChild variant="outline" size="lg" className="border-border text-foreground hover:bg-muted font-medium">
                <Link to="/ContactRequestServicePage">
                  Request on-site damage inspection
                  <ArrowRight className="size-4 ml-2" />
                </Link>
              </Button>
            </div>
          </motion.div>

          {/* Right Column: Documentary Field Photography */}
          <motion.div {...animationProps} transition={{
          duration: 0.4,
          delay: shouldReduceMotion ? 0 : 0.1
        }} className="lg:col-span-5 relative">
            <div className="relative rounded-md border border-border bg-muted p-2 shadow-md">
              <div className="relative aspect-[4/5] sm:aspect-[3/4] lg:aspect-[4/5] w-full overflow-hidden rounded-sm bg-muted">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1801371/img/fire_restoration_soot_removal_technician.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1801371/img/fire_restoration_soot_removal_technician.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1801371/img/fire_restoration_soot_removal_technician.png-wvc-sizes" alt="TriServe restoration technician using chemical dry sponge and HEPA soot vacuum on structural interior wall" loading="lazy" decoding="async" className="h-full w-full object-cover" />

                {/* Technical Inset Badging */}
                <div className="absolute top-3 left-3 bg-secondary/90 text-secondary-foreground backdrop-blur-xs px-2.5 py-1 rounded-sm text-[11px] font-mono font-medium tracking-wide border border-secondary-foreground/20">
                  SPEC: Chemical Dry-Chem Sponge & HEPA Air Wash
                </div>

                <div className="absolute bottom-3 inset-x-3 bg-background/95 text-foreground backdrop-blur-xs p-3 rounded-sm border border-border/80 shadow-xs">
                  <div className="flex items-start gap-2.5">
                    <div className="size-2 rounded-full bg-accent shrink-0 mt-1.5" />
                    <div>
                      <p className="text-xs font-semibold text-foreground">
                        Targeted surface soot neutralization
                      </p>
                      <p className="text-[11px] text-muted-foreground leading-tight mt-0.5">
                        Latex-based chemical sponges lift oily hydrocarbons without driving abrasive carbon deeper into drywall or millwork.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Insurance Direct Claim Coordination Note */}
            <div className="mt-4 p-3.5 rounded-md bg-muted/60 border border-border flex items-center justify-between text-xs">
              <span className="text-foreground font-medium">Direct adjuster documentation provided</span>
              <span className="font-mono text-[11px] text-primary font-semibold">100% Insurance compliant</span>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}